import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  Zap, ArrowLeft, Clock, Users, Code, Trophy, Calendar, Building2,
  Play, CheckCircle, Loader, Target, Star, ChevronRight, Briefcase,
  Award, AlertCircle, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ShareButtons from '../components/ShareButtons';
import { loginUrlWithRedirect } from '../utils/urlConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const difficultyColors = {
  easy: 'bg-green-500/20 text-green-400 border-green-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  hard: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const statusColors = {
  upcoming: 'bg-blue-500/20 text-blue-400',
  live: 'bg-green-500/20 text-green-400',
  ended: 'bg-gray-500/20 text-gray-400'
};

const CodeArenaHiringChallengesPage = () => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchChallenges();
  }, [statusFilter]);

  const fetchChallenges = async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API}/codearena/hiring-challenges${params}`);
      setChallenges(res.data.challenges || []);
    } catch (err) {
      console.error('Failed to fetch challenges');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeUntil = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = date - now;
    
    if (diff <= 0) return 'Started';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Link 
              to="/codearena" 
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={18} />
              Back to EvalentX Arena
            </Link>
          </div>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <Zap className="text-orange-400" size={32} />
                Hiring Challenges
              </h1>
              <p className="text-gray-400 mt-1">Compete in company-sponsored coding challenges</p>
            </div>
          </div>

          {/* Status Filters */}
          <div className="flex gap-3 mt-6">
            {['', 'upcoming', 'live', 'ended'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-orange-500 text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                {status === '' ? 'All' : status === 'live' ? '🔴 Live Now' : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Challenges List */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader className="animate-spin text-orange-400" size={32} />
          </div>
        ) : challenges.length > 0 ? (
          <div className="space-y-4">
            {challenges.map(challenge => (
              <Link
                key={challenge.id}
                to={`/codearena/hiring-challenges/${challenge.id}`}
                className="block bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 hover:border-orange-500/30 transition-all group"
                data-testid={`hiring-challenge-${challenge.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-white group-hover:text-orange-400 transition-colors">
                        {challenge.title}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[challenge.status]}`}>
                        {challenge.status === 'live' ? '🔴 Live' : challenge.status}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs border ${difficultyColors[challenge.difficulty]}`}>
                        {challenge.difficulty}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-gray-400 mb-3">
                      <Building2 size={16} />
                      <span className="font-medium">{challenge.company_name}</span>
                    </div>
                    
                    <p className="text-gray-500 text-sm mb-4 line-clamp-2">{challenge.description}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {challenge.duration_minutes} min
                      </span>
                      <span className="flex items-center gap-1">
                        <Users size={14} />
                        {challenge.participants_count || 0} participants
                      </span>
                      <span className="flex items-center gap-1">
                        <Code size={14} />
                        {challenge.problems?.length || 0} problems
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(challenge.start_time).toLocaleDateString()}
                      </span>
                    </div>
                    
                    {challenge.skills_tested?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {challenge.skills_tested.slice(0, 4).map((skill, idx) => (
                          <span key={idx} className="px-3 py-1 bg-orange-500/10 text-orange-400 rounded-lg text-xs border border-orange-500/20">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="text-right flex-shrink-0">
                    {challenge.status === 'upcoming' && (
                      <div className="text-orange-400 text-sm mb-2">
                        Starts in {formatTimeUntil(challenge.start_time)}
                      </div>
                    )}
                    <span className="inline-flex items-center gap-1 text-orange-400 text-sm group-hover:underline">
                      {challenge.status === 'live' ? 'Join Now' : 'View Details'}
                      <ChevronRight size={16} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-12 text-center">
            <Zap className="mx-auto text-gray-600 mb-4" size={48} />
            <h3 className="text-xl font-semibold text-white mb-2">No Hiring Challenges</h3>
            <p className="text-gray-500">
              {statusFilter ? `No ${statusFilter} challenges at the moment` : 'Check back later for company-sponsored challenges'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Challenge Detail Page
export const CodeArenaHiringChallengeDetailPage = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [myStatus, setMyStatus] = useState(null);

  const isLoggedIn = !!localStorage.getItem('codearena_token');
  const token = localStorage.getItem('codearena_token');

  useEffect(() => {
    fetchChallenge();
    if (isLoggedIn) {
      checkMyStatus();
    }
  }, [challengeId]);

  const fetchChallenge = async () => {
    try {
      const res = await axios.get(`${API}/codearena/hiring-challenges/${challengeId}`);
      setChallenge(res.data);
    } catch (err) {
      toast.error('Challenge not found');
      navigate('/codearena/hiring-challenges');
    } finally {
      setLoading(false);
    }
  };

  const checkMyStatus = async () => {
    try {
      const res = await axios.get(`${API}/codearena/hiring-challenges/${challengeId}/my-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyStatus(res.data);
    } catch (err) {
      console.error('Failed to check status');
    }
  };

  const handleRegister = async () => {
    if (!isLoggedIn) {
      navigate(loginUrlWithRedirect('/codearena/auth'));
      return;
    }

    setRegistering(true);
    try {
      await axios.post(
        `${API}/codearena/hiring-challenges/${challengeId}/register`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Registered successfully!');
      checkMyStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader className="animate-spin text-orange-400" size={32} />
      </div>
    );
  }

  if (!challenge) return null;

  const getStatus = () => {
    const now = new Date().toISOString();
    if (challenge.end_time && now > challenge.end_time) return 'ended';
    if (challenge.start_time && now >= challenge.start_time) return 'live';
    return 'upcoming';
  };

  const status = getStatus();

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <Link 
            to="/codearena/hiring-challenges" 
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft size={18} />
            Back to Challenges
          </Link>
          
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[status]}`}>
                  {status === 'live' ? '🔴 Live Now' : status === 'upcoming' ? '📅 Upcoming' : 'Ended'}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm border ${difficultyColors[challenge.difficulty]}`}>
                  {challenge.difficulty}
                </span>
              </div>
              
              <h1 className="text-3xl font-bold text-white mb-3">{challenge.title}</h1>
              
              <div className="flex items-center gap-2 text-gray-300 mb-2">
                <Building2 size={18} className="text-orange-400" />
                <span className="text-lg font-medium">{challenge.company_name}</span>
              </div>
              
              {/* Job Role if available */}
              {challenge.job_role && (
                <div className="flex items-center gap-2 text-gray-400 mb-3">
                  <Briefcase size={16} className="text-purple-400" />
                  <span className="font-medium">{challenge.job_role}</span>
                  {challenge.job_type && (
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">
                      {challenge.job_type}
                    </span>
                  )}
                </div>
              )}
              
              <p className="text-gray-400 mb-4">{challenge.description}</p>
              
              {/* Deadline Alert */}
              {challenge.end_time && status !== 'ended' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400 text-sm mb-4">
                  <AlertCircle size={16} />
                  <span>Deadline: {new Date(challenge.end_time).toLocaleString()}</span>
                </div>
              )}
              
              <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Clock size={16} />
                  {challenge.duration_minutes} minutes
                </span>
                <span className="flex items-center gap-1">
                  <Users size={16} />
                  {challenge.participants_count || 0} participants
                </span>
                <span className="flex items-center gap-1">
                  <Code size={16} />
                  {challenge.problems?.length || 0} problems
                </span>
              </div>
            </div>
            
            <div className="flex flex-col gap-3">
              {/* Share Button */}
              <ShareButtons
                title={challenge.title}
                description={challenge.description}
                url={`/codearena/hiring-challenges/${challengeId}`}
                shareText={`🏆 ${challenge.title} by ${challenge.company_name} | Hiring Challenge | ${challenge.duration_minutes} min | Join now on EvalentX!`}
                type="challenge"
              />
              
              {myStatus?.registered ? (
                <>
                  <div className="flex items-center gap-2 px-6 py-3 bg-green-500/20 text-green-400 rounded-xl border border-green-500/30">
                    <CheckCircle size={20} />
                    Registered
                  </div>
                  {status === 'live' && (
                    <Link
                      to={`/codearena/assessment/${challengeId}/start`}
                      className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium hover:from-green-600 hover:to-emerald-700 transition-all"
                      data-testid="start-assessment-btn"
                    >
                      <Play size={18} />
                      Start Assessment
                    </Link>
                  )}
                  {status === 'upcoming' && (
                    <div className="px-6 py-3 bg-blue-500/20 text-blue-400 rounded-xl border border-blue-500/30 text-center text-sm">
                      Assessment will be available when challenge goes live
                    </div>
                  )}
                </>
              ) : status !== 'ended' ? (
                <button
                  onClick={handleRegister}
                  disabled={registering}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-medium hover:from-orange-600 hover:to-red-700 transition-all disabled:opacity-50"
                  data-testid="register-challenge-btn"
                >
                  {registering ? (
                    <Loader className="animate-spin" size={18} />
                  ) : (
                    <>
                      <Play size={18} />
                      {isLoggedIn ? 'Register Now' : 'Login to Register'}
                    </>
                  )}
                </button>
              ) : (
                <div className="px-6 py-3 bg-gray-500/20 text-gray-400 rounded-xl">
                  Challenge Ended
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Skills Tested */}
            {challenge.skills_tested?.length > 0 && (
              <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
                <h2 className="text-xl font-bold text-white mb-4">Skills Tested</h2>
                <div className="flex flex-wrap gap-2">
                  {challenge.skills_tested.map((skill, idx) => (
                    <span 
                      key={idx}
                      className="px-4 py-2 bg-orange-500/10 text-orange-400 rounded-lg border border-orange-500/20"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Participant Status */}
            {myStatus?.registered && myStatus.participant && (
              <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
                <h2 className="text-xl font-bold text-white mb-4">Your Progress</h2>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-white/5 rounded-xl">
                    <p className="text-2xl font-bold text-orange-400">{myStatus.participant.score || 0}</p>
                    <p className="text-sm text-gray-500">Score</p>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-xl">
                    <p className="text-2xl font-bold text-green-400">{myStatus.participant.problems_solved || 0}</p>
                    <p className="text-sm text-gray-500">Solved</p>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-xl">
                    <p className="text-2xl font-bold text-purple-400">{myStatus.participant.rank || '-'}</p>
                    <p className="text-sm text-gray-500">Rank</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Challenge Info */}
            <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
              <h3 className="font-semibold text-white mb-4">Challenge Details</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Start Time</p>
                  <p className="text-white font-medium">
                    {new Date(challenge.start_time).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Duration</p>
                  <p className="text-white font-medium">{challenge.duration_minutes} minutes</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Problems</p>
                  <p className="text-white font-medium">{challenge.problems?.length || 0} problems</p>
                </div>
                {challenge.max_participants && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Max Participants</p>
                    <p className="text-white font-medium">{challenge.max_participants}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Company Card */}
            <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
                  <Building2 className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{challenge.company_name}</h3>
                  <p className="text-sm text-gray-500">Hiring Challenge</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeArenaHiringChallengesPage;
